import { getWorldMapObject } from '@blocksuite/affine/model';
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import { nanoid, type Store } from '@blocksuite/affine/store';
import { useEffect, useRef, useState } from 'react';
import * as Y from 'yjs';

import type { AffineEditorContainer } from './blocksuite-editor';
import * as styles from './object-annotation.css';
import {
  ANNOTATION_KEY,
  type AnnotationChatMessage,
  type AnnotationTarget,
  getAnnotation,
} from './object-annotation-store';
import { resolveAnnotationTarget } from './object-annotation-target';

const RUNS = 'mpxAiIterations',
  LOGS = 'mpxAiRunLogs',
  STALE = 5 * 60 * 1000;
interface Run {
  state: string;
  created: string;
  queuedAt?: string;
  heartbeat?: string;
  started?: string;
  attempt?: number;
  objects: { id: string; conversation: unknown[] }[];
}
interface Schedule {
  enabled: boolean;
  due: string | null;
  timezone: string | null;
  state: string;
}
interface LogEntry {
  time: string;
  message: string;
  objectId?: string;
  level: string;
  attempt: number;
}
interface Control {
  csrf: string;
  enabled: boolean;
  subscribed: boolean;
  schedules: Record<string, Schedule>;
}
function plain<T>(v: unknown): T {
  return (v instanceof Y.Map ? v.toJSON() : v) as T;
}
function annotation(target: AnnotationTarget) {
  return (
    plain<{
      aiArmed?: boolean;
      aiIteration?: string;
      aiSchedule?: Schedule;
      chat?: Record<string, AnnotationChatMessage>;
    }>(target.data.get(ANNOTATION_KEY)) ?? {}
  );
}
function targets(editor: AffineEditorContainer): AnnotationTarget[] {
  const ids = new Set<string>();
  const visit = (m: NonNullable<Store['root']>) => {
    ids.add(m.id);
    m.children.forEach(visit);
  };
  if (editor.doc.root) visit(editor.doc.root);
  editor.std
    ?.getOptional(GfxControllerIdentifier)
    ?.surface?.elementModels.forEach(e => ids.add(e.id));
  return [...ids]
    .map(id => resolveAnnotationTarget(editor, id))
    .filter((t): t is AnnotationTarget => t !== null);
}
function name(target: AnnotationTarget) {
  return (
    getWorldMapObject(String(target.data.get('mpxWorldMapKind') ?? ''))?.name ??
    (String(target.data.get('prop:name') ?? target.name) || target.kind)
  );
}
function status(run: Run | undefined, now: number) {
  if (!run || !['queued', 'running'].includes(run.state)) return 'idle';
  const last = Date.parse(
    run.heartbeat ?? run.started ?? run.queuedAt ?? run.created
  );
  return !Number.isFinite(last) || now - last >= STALE
    ? 'stale'
    : run.state === 'queued'
      ? 'waiting for AI'
      : 'running';
}
function queue(editor: AffineEditorContainer, ids: Set<string>) {
  const parent = editor.doc,
    root = parent.root;
  if (!root || parent.readonly) return;
  const active = plain<Record<string, Run>>(root.yBlock.get(RUNS)) ?? {};
  const busy = new Set(
    Object.values(active)
      .filter(r => ['queued', 'running'].includes(r.state))
      .flatMap(r => r.objects.map(o => o.id))
  );
  const items = targets(editor).filter(
    t => ids.has(t.id) && annotation(t).aiArmed && !busy.has(t.id)
  );
  if (!items.length) return;
  const id = nanoid(),
    now = new Date().toISOString();
  parent.captureSync();
  parent.transact(() => {
    let runs = root.yBlock.get(RUNS);
    if (!(runs instanceof Y.Map)) {
      runs = new Y.Map();
      root.yBlock.set(RUNS, runs);
    }
    const objects = items.map(t => {
      const a = getAnnotation(t, parent);
      if (!a) throw new Error('Object is no longer editable.');
      a.set('aiIteration', id);
      return {
        id: t.id,
        conversation: Object.entries(
          plain<Record<string, AnnotationChatMessage>>(a.get('chat')) ?? {}
        )
          .map(([messageId, m]) => ({ id: messageId, ...m }))
          .sort(
            (a, b) => a.time.localeCompare(b.time) || a.id.localeCompare(b.id)
          ),
      };
    });
    (runs as Y.Map<Run>).set(id, {
      state: 'queued',
      created: now,
      queuedAt: now,
      attempt: 0,
      objects,
    });
    let logs = root.yBlock.get(LOGS);
    if (!(logs instanceof Y.Map)) {
      logs = new Y.Map();
      root.yBlock.set(LOGS, logs);
    }
    const entries = new Y.Map<LogEntry>();
    entries.set('queued', {
      time: now,
      message: 'Queued for AI.',
      level: 'info',
      attempt: 0,
    });
    (logs as Y.Map<Y.Map<LogEntry>>).set(id, entries);
  });
  return id;
}

export function AIRunManager({
  editor,
  readonly,
}: {
  editor: AffineEditorContainer;
  readonly: boolean;
}) {
  const [open, setOpen] = useState(false),
    [items, setItems] = useState<AnnotationTarget[]>([]),
    [runs, setRuns] = useState<Record<string, Run>>({}),
    [now, setNow] = useState(Date.now());
  const [selected, setSelected] = useState<Set<string>>(new Set()),
    [active, setActive] = useState(''),
    [tab, setTab] = useState<'Chat' | 'Log'>('Chat'),
    [chosenRun, setChosenRun] = useState('');
  const [drafts, setDrafts] = useState<Record<string, string>>({}),
    [hours, setHours] = useState<Record<string, string>>({});
  const [control, setControl] = useState<Control | null>(null),
    [error, setError] = useState(''),
    [pending, setPending] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null),
    seen = useRef(new Set<string>()),
    chatEnd = useRef<HTMLDivElement>(null);
  const disabled = readonly || editor.doc.readonly,
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  useEffect(() => {
    let stopped = false;
    const update = () => {
      if (stopped) return;
      const list = targets(editor).filter(t => annotation(t).aiArmed === true);
      setItems(list);
      setRuns({
        ...plain<Record<string, Run>>(editor.doc.root?.yBlock.get(RUNS)),
      });
      const additions = list.filter(t => !seen.current.has(t.id));
      if (additions.length)
        setSelected(s => new Set([...s, ...additions.map(t => t.id)]));
      seen.current = new Set(list.map(t => t.id));
    };
    editor.updateComplete.then(update).catch(console.error);
    editor.doc.spaceDoc.on('afterTransaction', update);
    return () => {
      stopped = true;
      editor.doc.spaceDoc.off('afterTransaction', update);
    };
  }, [editor]);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (open) dialog.current?.showModal();
  }, [open]);
  useEffect(() => {
    if (!items.some(t => t.id === active)) {
      setActive(items[0]?.id ?? '');
      setChosenRun('');
    }
  }, [items, active]);
  useEffect(() => {
    if (!open) return;
    let stopped = false,
      first = true;
    setControl(null);
    const refresh = () =>
      fetch(
        '/ai-bridge/api/run-control?document_id=' +
          encodeURIComponent(editor.doc.id)
      )
        .then(async r => {
          const value = await r.json();
          if (!r.ok) throw new Error(value.error ?? 'AI bridge unavailable');
          if (!stopped) {
            setControl(value);
            if (first) setError('');
            first = false;
          }
        })
        .catch(e => {
          if (!stopped) {
            setControl(null);
            setError(e.message);
            first = false;
          }
        });
    refresh().catch(console.error);
    const timer = setInterval(() => {
      refresh().catch(console.error);
    }, 5000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [open, editor]);
  const scheduleFor = (t: AnnotationTarget) =>
    control?.schedules?.[t.id] ?? annotation(t).aiSchedule;
  const current = items.find(t => t.id === active),
    a = current ? annotation(current) : {},
    chat = Object.entries(a.chat ?? {}).sort(
      ([x, m], [y, n]) => m.time.localeCompare(n.time) || x.localeCompare(y)
    );
  const objectRuns = Object.entries(runs)
    .filter(([, r]) => r.objects.some(o => o.id === active))
    .sort(([, a], [, b]) =>
      (b.queuedAt ?? b.created).localeCompare(a.queuedAt ?? a.created)
    );
  const runId =
    chosenRun && runs[chosenRun]?.objects.some(o => o.id === active)
      ? chosenRun
      : (objectRuns[0]?.[0] ?? '');
  const run = runs[runId],
    runStatus = status(run, now);
  const logs = Object.entries(
    plain<Record<string, LogEntry>>(
      plain<Record<string, unknown>>(editor.doc.root?.yBlock.get(LOGS))?.[runId]
    ) ?? {}
  ).sort(
    ([x, m], [y, n]) => m.time.localeCompare(n.time) || x.localeCompare(y)
  );
  useEffect(() => {
    if (open && tab === 'Chat')
      chatEnd.current?.scrollIntoView({ block: 'nearest' });
  }, [open, tab, active, chat.length]);
  const busy = new Set(
    Object.values(runs)
      .filter(r => ['queued', 'running'].includes(r.state))
      .flatMap(r => r.objects.map(o => o.id))
  );
  const count = items.filter(t => selected.has(t.id) && !busy.has(t.id)).length;
  const today = new Date(now),
    availableHours = Array.from(
      { length: 24 },
      (_, i) => String(i).padStart(2, '0') + ':00'
    ).filter(h => {
      const d = new Date(now);
      d.setHours(Number(h.slice(0, 2)), 0, 0, 0);
      return d.getTime() > now;
    });
  const request = async (body: Record<string, unknown>) => {
    if (!control?.enabled)
      throw new Error(
        'Enable this document in AI bridge before scheduling or retrying.'
      );
    const r = await fetch('/ai-bridge/api/run-control', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Bridge-CSRF': control.csrf,
      },
      body: JSON.stringify({ document_id: editor.doc.id, ...body }),
    });
    const value = await r.json();
    if (!r.ok) throw new Error(value.error ?? 'Run control failed');
    if (typeof body.object_id === 'string')
      setControl(c =>
        c
          ? {
              ...c,
              schedules: { ...c.schedules, [String(body.object_id)]: value },
            }
          : c
      );
    return value;
  };
  const perform = async (action: () => Promise<unknown>) => {
    setPending(true);
    setError('');
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Run control failed');
    } finally {
      setPending(false);
    }
  };
  const hourFor = (t: AnnotationTarget) =>
    hours[t.id] ??
    (scheduleFor(t)?.due
      ? new Date(scheduleFor(t)?.due ?? '').toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
        })
      : (availableHours[0] ?? ''));
  const schedule = (t: AnnotationTarget, enabled: boolean, hour = hourFor(t)) =>
    perform(async () => {
      const due = new Date();
      due.setHours(Number(hour.slice(0, 2)), 0, 0, 0);
      if (enabled && (!hour || due.getTime() <= Date.now()))
        throw new Error('Choose a future hour today.');
      await request({
        action: 'schedule',
        object_id: t.id,
        enabled,
        due: due.toISOString(),
        timezone: zone,
      });
    });
  const unarm = (t: AnnotationTarget) =>
    perform(async () => {
      if (control?.enabled) await request({ action: 'unarm', object_id: t.id });
      else {
        editor.doc.captureSync();
        editor.doc.transact(() =>
          getAnnotation(t, editor.doc)?.set('aiArmed', false)
        );
      }
    });
  const send = () => {
    if (!current || disabled) return;
    const content = (drafts[active] ?? '').trim();
    if (!content) return;
    editor.doc.captureSync();
    editor.doc.transact(() => {
      const annotation = getAnnotation(current, editor.doc),
        chat = annotation?.get('chat');
      if (chat instanceof Y.Map)
        chat.set(nanoid(), {
          role: 'user',
          content,
          time: new Date().toISOString(),
        } satisfies AnnotationChatMessage);
    });
    setDrafts(s => ({ ...s, [active]: '' }));
  };
  return (
    <div className={styles.globalAI}>
      <button
        className={styles.smallButton}
        onClick={() => {
          setOpen(true);
          setError('');
        }}
        data-testid="run-ai-open"
      >
        Run AI
      </button>
      <span className={styles.kind}>{items.length} armed</span>
      {open && (
        <dialog
          ref={dialog}
          className={styles.runDialog}
          aria-label="Run AI"
          onKeyDown={e => e.stopPropagation()}
          onCancel={() => setOpen(false)}
          onClose={() => setOpen(false)}
        >
          <header className={styles.chatHeader}>
            <div>
              <strong>Run AI</strong>
              <span className={styles.kind}>
                {' '}
                · {items.length} armed objects
              </span>
            </div>
            <button
              className={styles.iconButton}
              aria-label="Close Run AI"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
          </header>
          <div className={styles.runBody}>
            <aside className={styles.runList} aria-label="Armed objects">
              <div className={styles.runListHeader}>
                <strong>Armed objects</strong>
                <small>
                  Today · {today.toLocaleDateString()} · {zone}
                </small>
                <span className={styles.kind}>
                  Select objects for the next run, or schedule an hour today.
                </span>
              </div>
              <div className={styles.runEntries}>
                {!items.length && (
                  <p className={styles.chatHint}>
                    No armed objects. Arm an object in Annotations to add it
                    here.
                  </p>
                )}
                {items.map(t => {
                  const scheduleValue = scheduleFor(t);
                  const latest = Object.values(runs)
                    .filter(r => r.objects.some(o => o.id === t.id))
                    .sort((a, b) =>
                      (b.queuedAt ?? b.created).localeCompare(
                        a.queuedAt ?? a.created
                      )
                    )[0];
                  return (
                    <article
                      key={t.id}
                      className={styles.runEntry}
                      data-selected={active === t.id}
                      data-object-id={t.id}
                    >
                      <button
                        className={styles.runObjectName}
                        onClick={() => {
                          setActive(t.id);
                          setChosenRun('');
                        }}
                      >
                        <strong>{name(t)}</strong>
                        <small>
                          {t.kind} · {t.id} · {status(latest, now)}
                        </small>
                      </button>
                      <div className={styles.runObjectActions}>
                        <label>
                          <input
                            type="checkbox"
                            aria-label={'Select ' + name(t) + ' for next run'}
                            checked={selected.has(t.id)}
                            disabled={disabled || pending}
                            onChange={e =>
                              setSelected(s => {
                                const n = new Set(s);
                                if (e.target.checked) n.add(t.id);
                                else n.delete(t.id);
                                return n;
                              })
                            }
                          />
                          Next run
                        </label>
                        <button
                          className={styles.smallButton}
                          disabled={disabled || pending}
                          onClick={() => {
                            unarm(t).catch(console.error);
                          }}
                        >
                          Un-arm
                        </button>
                      </div>
                      <div className={styles.runObjectActions}>
                        <label>
                          <input
                            type="checkbox"
                            aria-label={'Schedule ' + name(t)}
                            checked={scheduleValue?.enabled === true}
                            disabled={
                              disabled ||
                              pending ||
                              !control?.enabled ||
                              (!availableHours.length &&
                                !scheduleValue?.enabled)
                            }
                            onChange={e => {
                              schedule(t, e.target.checked).catch(
                                console.error
                              );
                            }}
                          />
                          Schedule
                        </label>
                        <select
                          aria-label={'Run hour for ' + name(t)}
                          value={hourFor(t)}
                          disabled={disabled || pending || !control?.enabled}
                          onChange={e => {
                            const h = e.target.value;
                            setHours(s => ({ ...s, [t.id]: h }));
                            if (scheduleValue?.enabled)
                              schedule(t, true, h).catch(console.error);
                          }}
                        >
                          {!availableHours.length && !scheduleValue?.due && (
                            <option value="">No hours left today</option>
                          )}
                          {Array.from(
                            { length: 24 },
                            (_, i) => String(i).padStart(2, '0') + ':00'
                          ).map(h => (
                            <option
                              key={h}
                              disabled={!availableHours.includes(h)}
                            >
                              {h}
                            </option>
                          ))}
                        </select>
                      </div>
                      {scheduleValue && (
                        <small className={styles.kind}>
                          {scheduleValue.state}
                          {scheduleValue.enabled
                            ? ' · today ' + hourFor(t)
                            : ''}
                        </small>
                      )}
                    </article>
                  );
                })}
              </div>
              <footer className={styles.runListFooter}>
                <button
                  className={styles.saveButton}
                  data-testid="run-ai-start"
                  disabled={disabled || pending || !count}
                  onClick={() => {
                    queue(editor, selected);
                    setTab('Log');
                    setChosenRun('');
                  }}
                >
                  Run selected ({count})
                </button>
                <span className={styles.kind}>
                  Already queued or running objects are not queued twice. Un-arm
                  cancels schedules; queued work remains.
                </span>
              </footer>
            </aside>
            <section className={styles.runDetail}>
              <header className={styles.runDetailHeader}>
                <strong>{current ? name(current) : 'Select an object'}</strong>
                <span
                  className={styles.runState}
                  data-state={runStatus}
                  role="status"
                  data-testid="ai-run-status"
                >
                  {runStatus}
                </span>
                {runStatus === 'stale' && (
                  <button
                    className={styles.smallButton}
                    disabled={disabled || pending || !control?.enabled}
                    onClick={() => {
                      perform(() =>
                        request({ action: 'retry', iteration_id: runId })
                      ).catch(console.error);
                    }}
                  >
                    Retry
                  </button>
                )}
              </header>
              <nav
                className={styles.runTabs}
                role="tablist"
                aria-label="Object AI details"
              >
                {(['Chat', 'Log'] as const).map(t => (
                  <button
                    key={t}
                    role="tab"
                    aria-selected={tab === t}
                    className={styles.smallButton}
                    onClick={() => setTab(t)}
                  >
                    {t}
                  </button>
                ))}
              </nav>
              {tab === 'Chat' ? (
                <div
                  className={styles.runTabPanel}
                  role="tabpanel"
                  aria-label="Chat"
                >
                  <p className={styles.chatHint}>
                    Messages are saved with this object. Save your next steps,
                    then select it for Run AI.
                  </p>
                  <div className={styles.runMessages} aria-live="polite">
                    {chat.map(([id, m]) => (
                      <article
                        className={styles.chatMessage}
                        key={id}
                        data-role={m.role}
                      >
                        <strong>
                          {m.role === 'user'
                            ? 'You'
                            : m.role === 'assistant'
                              ? 'AI'
                              : 'System'}
                        </strong>
                        <p>{m.content}</p>
                        <time>{new Date(m.time).toLocaleString()}</time>
                      </article>
                    ))}
                    {!chat.length && (
                      <p className={styles.chatHint}>No messages yet.</p>
                    )}
                    <div ref={chatEnd} />
                  </div>
                  <form
                    className={styles.chatComposer}
                    onSubmit={e => {
                      e.preventDefault();
                      send();
                    }}
                  >
                    <textarea
                      aria-label="Chat message"
                      placeholder="Write next steps for this object…"
                      disabled={disabled || !current}
                      value={drafts[active] ?? ''}
                      onChange={e =>
                        setDrafts(s => ({ ...s, [active]: e.target.value }))
                      }
                    />
                    <button
                      className={styles.saveButton}
                      disabled={
                        disabled || !current || !(drafts[active] ?? '').trim()
                      }
                    >
                      Save message
                    </button>
                  </form>
                </div>
              ) : (
                <div
                  className={styles.runTabPanel}
                  role="tabpanel"
                  aria-label="Log"
                >
                  <div className={styles.runLogHeader}>
                    <label>
                      Run{' '}
                      <select
                        aria-label="Iteration"
                        value={runId}
                        onChange={e => setChosenRun(e.target.value)}
                      >
                        {objectRuns.map(([id, r]) => (
                          <option key={id} value={id}>
                            {new Date(r.created).toLocaleString()} · {r.state}
                            {r.attempt ? ' · retry ' + r.attempt : ''}
                          </option>
                        ))}
                        {!runId && <option value="">No runs yet</option>}
                      </select>
                    </label>
                    <small className={styles.kind}>
                      {run ? 'Result: ' + run.state : 'idle'} · Stale after 5
                      minutes without a response
                    </small>
                  </div>
                  <div
                    className={styles.runMessages}
                    role="log"
                    aria-label="AI activity log"
                    aria-live="polite"
                  >
                    {logs.map(([id, l]) => (
                      <article
                        className={styles.chatMessage}
                        key={id}
                        data-level={l.level}
                      >
                        <time>
                          {new Date(l.time).toLocaleString()}
                          {l.objectId
                            ? ' · ' +
                              (items.find(t => t.id === l.objectId)?.name ??
                                l.objectId)
                            : ''}
                        </time>
                        <p>{l.message}</p>
                      </article>
                    ))}
                    {!logs.length && (
                      <p className={styles.chatHint}>
                        No activity yet. A connected AI runner posts updates
                        here while working.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </section>
          </div>
          <footer className={styles.runFooter}>
            {error && <span role="alert">{error}</span>}
            {control && !control.enabled && (
              <span>
                Enable this document in AI bridge to dispatch runs and
                schedules.
              </span>
            )}
            {control?.enabled && !control.subscribed && (
              <span>
                No active AI subscription for this document. Runs wait until a
                runner checks the queue.
              </span>
            )}
            <a href="/ai-bridge" target="_blank" rel="noopener noreferrer">
              AI bridge
            </a>
          </footer>
        </dialog>
      )}
    </div>
  );
}
